/**
 * Phone preparing pond: prep day, the four milestone bars, "+ Application",
 * "+ Water check" (ad hoc, any subset of parameters, #212/#244),
 * "Mark a milestone" (Done today, confirmed, since a technician can't undo it),
 * "Mark as stocked" (Farm Technician and above), and the prep journal by Prep day.
 */
import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '@/lib/cn';
import { qk } from '@/lib/queryKeys';
import { displayPhDate, todayInPH } from '@/lib/phDate';
import { prepDayOn, type Crop, type PondFacts } from '@/lib/crop';
import { useAuth } from '@/hooks/useAuth';
import { useMembership } from '@/hooks/useMembership';
import { amountText } from '@/components/pond/page/format';
import { WaterCheckForm } from '@/components/pond/page/WaterCheckForm';
import { MarkStockedDialog } from '@/components/pond/page/PrestockingTab';
import {
  canEditCheck,
  checkDay,
  checkEntries,
  colorLabel,
  useWaterCheckMutations,
  useWaterChecks,
} from '@/components/pond/page/waterChecks';
import type { WaterCheckResponse } from '@/types/generated/openapi';
import {
  prepJournalDays,
  PREP_MILESTONES,
  useMilestones,
  usePrepApplications,
  putMilestone,
  type MilestoneKind,
} from '@/components/pond/page/data';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { MILESTONE_HINT, TECHNICIAN_PREP_NOTE } from '@/components/pond/page/prepCopy';
import { PhoneSheet } from './PhoneSheet';
import { displayPhTime } from '@/lib/phDate';
import { PhoneMenuButton } from '@/components/layout/PhoneMenuButton';

const BAR_LABEL: Record<MilestoneKind, string> = {
  water_in: 'Water in',
  culture_start: 'Culture',
  stocking_prep: 'Stocking prep',
};

const short = (d: string) => displayPhDate(d, { month: 'short', day: 'numeric' });

interface Props {
  farmId: string;
  pondId: string;
  pondName: string;
  /** For the Mark as stocked dialog (name, area for the density line). */
  pond: PondFacts;
  crop: Crop;
  /** Open the water-check screen on arrival (the Today card's "+ Water check"). */
  startWithCheck?: boolean;
}

export function PhonePrep({ farmId, pondId, pondName, pond, crop, startWithCheck = false }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const today = todayInPH();
  const { data: milestones = [], isLoading: milestonesLoading, isSuccess: milestonesLoaded } = useMilestones(farmId, crop.id);
  const { apps, loading: appsLoading } = usePrepApplications(farmId, crop.id);
  const { checks, loading: checksLoading } = useWaterChecks(farmId, crop.id);
  const { user } = useAuth();
  const { isSupervisorOrAbove, isTechnician } = useMembership();
  const [checkScreen, setCheckScreen] = useState<{ editing: WaterCheckResponse | null } | null>(
    startWithCheck ? { editing: null } : null
  );
  const [sheetOpen, setSheetOpen] = useState(false);
  const [stockOpen, setStockOpen] = useState(false);
  const [confirmKind, setConfirmKind] = useState<MilestoneKind | null>(null);
  const closeSheet = () => {
    setSheetOpen(false);
    setConfirmKind(null);
  };

  const byKind = new Map(milestones.map((m) => [m.kind, m]));
  const undone = PREP_MILESTONES.filter((x) => !byKind.get(x.kind)?.done_on);
  const waterIn = byKind.get('water_in')?.done_on ?? crop.prep_start_date ?? null;
  const prepDay = prepDayOn(crop, today);
  const days = crop.prep_start_date ? prepJournalDays(crop, milestones, apps, true, checks) : [];

  const markDone = useMutation({
    mutationFn: ({ kind }: { kind: MilestoneKind }) => putMilestone(queryClient, farmId, crop.id, kind, { done_on: today }),
    onSuccess: (_r, { kind }) => {
      toast.success(`${PREP_MILESTONES.find((x) => x.kind === kind)?.label} done today`);
      closeSheet();
      queryClient.invalidateQueries({ queryKey: qk.milestones.crop(farmId, crop.id) });
      queryClient.invalidateQueries({ queryKey: qk.pond.all(farmId) });
    },
    onError: (e: Error) => {
      toast.error(e.message || 'Not saved');
      queryClient.invalidateQueries({ queryKey: qk.milestones.crop(farmId, crop.id) });
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-100" data-phone-prep>
      <header className="shrink-0 space-y-2.5 border-b border-gray-200 bg-white px-4 pb-3 pt-2">
        <div className="flex items-center gap-2">
          <Link
            to="/entry"
            search={{}}
            aria-label="Back to Today"
            className="-ml-2 flex min-h-11 min-w-11 items-center justify-center rounded-lg text-gray-900 active:bg-gray-100"
          >
            <ChevronLeft className="h-6 w-6" aria-hidden="true" />
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-[17px] font-bold text-gray-900">{pondName}</h1>
            <p className="text-xs text-gray-500">{waterIn ? `Water in ${short(waterIn)}` : 'Preparing'}</p>
          </div>
          {prepDay !== null && (
            <span className="shrink-0 rounded-full bg-primary-100 px-2.5 py-1 text-xs font-semibold text-primary-800">
              Prep day {prepDay}
            </span>
          )}
          <PhoneMenuButton />
        </div>
        <ol className="grid grid-cols-4 gap-1 text-[11px] font-semibold leading-tight text-gray-600" aria-label="Milestones">
          {PREP_MILESTONES.map(({ kind }) => {
            const done = !!byKind.get(kind)?.done_on;
            return (
              <li key={kind} className="flex min-w-0 flex-col gap-1">
                <span className={cn('h-1.5 rounded-full', done ? 'bg-primary-600' : 'bg-gray-300')} aria-hidden="true" />
                <span className="break-words">
                  {BAR_LABEL[kind]}
                  {done ? ' ✓' : ''}
                  <span className="sr-only">{done ? ', done' : ', not done'}</span>
                </span>
              </li>
            );
          })}
          <li className="flex min-w-0 flex-col gap-1">
            <span className="h-1.5 rounded-full bg-amber-300" aria-hidden="true" />
            <span className="break-words">
              Stocking
              {(crop.planned_stocking_date || isSupervisorOrAbove) && (
                <span className="block font-normal">{crop.planned_stocking_date ? short(crop.planned_stocking_date) : 'not planned'}</span>
              )}
            </span>
          </li>
        </ol>
      </header>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-3">
        <Link
          to="/entry/pond/$pondId/application"
          params={{ pondId }}
          search={{}}
          className="flex min-h-[52px] items-center justify-center rounded-xl bg-primary-600 text-[15px] font-semibold text-white"
        >
          + Application
        </Link>
        <button
          type="button"
          onClick={() => setCheckScreen({ editing: null })}
          className="flex min-h-[52px] w-full items-center justify-center rounded-xl border border-primary-600 bg-white text-[15px] font-semibold text-primary-700"
        >
          + Water check
        </button>
        {/* Only once loaded: an unloaded list reads as "nothing done", and the upsert would drop planned_on. */}
        {milestonesLoaded && undone.length > 0 && (
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className="flex min-h-[52px] w-full items-center justify-between rounded-xl border border-gray-300 bg-white px-3.5 text-[15px] font-semibold text-gray-900"
          >
            <span>Mark a milestone</span>
            <span className="flex items-center gap-1 text-[13px] font-normal text-gray-500">
              {undone[0].label}
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </span>
          </button>
        )}

        {isSupervisorOrAbove && (
          <button
            type="button"
            onClick={() => setStockOpen(true)}
            className="flex min-h-[52px] w-full items-center justify-center rounded-xl border border-amber-700 bg-amber-700 text-[15px] font-semibold text-white"
            data-mark-stocked
          >
            Mark as stocked
          </button>
        )}

        {isTechnician && <p className="px-1 text-[13px] leading-snug text-gray-600">{TECHNICIAN_PREP_NOTE}</p>}

        <section aria-labelledby="pp-journal" className="rounded-2xl border border-gray-200 bg-white px-3.5 py-3" data-prep-journal>
          <h2 id="pp-journal" className="mb-1 text-[15px] font-bold text-gray-900">
            Prep journal
          </h2>
          {milestonesLoading || appsLoading || checksLoading ? (
            <LoadingSpinner />
          ) : (
            <ol>
              {days.map((d) => (
                <li
                  key={`${d.day}|${d.date}`}
                  className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-2.5 border-t border-gray-100 py-2.5 text-sm first:border-t-0"
                >
                  <span className="tabular-nums">
                    <b>{d.day > 0 ? `Prep ${d.day}` : 'Before'}</b>
                    <br />
                    <span className="text-xs text-gray-500">{d.date === today ? 'today' : short(d.date)}</span>
                  </span>
                  <span className="flex min-w-0 flex-col gap-1">
                    {d.milestones.map((m) => (
                      <span key={m} className="self-start rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-800">
                        {m}
                      </span>
                    ))}
                    {d.checks.map((c) => {
                      const entries = checkEntries(c);
                      const color = colorLabel(c.water_color);
                      const body = (
                        <>
                          <b>
                            Water check · {d.day === 0 ? `${short(checkDay(c))}, ` : ''}
                            {displayPhTime(c.measured_at)}
                          </b>
                          <span className="block tabular-nums">{entries.map((e) => `${e.short} ${e.text}`).join(' · ')}</span>
                          {(color || c.notes) && <span className="block text-gray-500">{[color, c.notes].filter(Boolean).join(' · ')}</span>}
                        </>
                      );
                      return canEditCheck(c, isSupervisorOrAbove, user?.id) ? (
                        <button
                          key={c.id}
                          type="button"
                          aria-label={`Edit the ${displayPhTime(c.measured_at)} water check`}
                          onClick={() => setCheckScreen({ editing: c })}
                          className="-mx-1.5 min-h-11 rounded-lg px-1.5 py-1 text-left text-gray-900 active:bg-gray-100"
                        >
                          {body}
                        </button>
                      ) : (
                        <span key={c.id} className="break-words text-gray-900">
                          {body}
                        </span>
                      );
                    })}
                    {d.applications.length > 0 ? (
                      <span className="break-words text-gray-900">
                        {d.applications.map((a) => `${a.product_name} ${amountText(a.amount, a.unit)}`).join(' · ')}
                      </span>
                    ) : (
                      d.milestones.length === 0 && d.checks.length === 0 && <span className="text-gray-500">Nothing yet</span>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>

      {stockOpen && <MarkStockedDialog open onClose={() => setStockOpen(false)} farmId={farmId} pond={pond} crop={crop} phone />}

      {checkScreen && (
        <PhoneWaterCheck
          farmId={farmId}
          crop={crop}
          editing={checkScreen.editing}
          canDelete={isSupervisorOrAbove}
          onClose={() => {
            setCheckScreen(null);
            // Drop the shortcut so a reload doesn't reopen an empty form.
            if (startWithCheck) navigate({ to: '/entry/pond/$pondId', params: { pondId }, search: (prev) => ({ ...prev, check: undefined }), replace: true });
          }}
        />
      )}

      {sheetOpen && milestonesLoaded && (
        <PhoneSheet label="Mark a milestone" onClose={closeSheet}>
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-900">Mark a milestone</h2>
              <button
                type="button"
                aria-label="Close"
                onClick={closeSheet}
                className="flex min-h-11 min-w-11 items-center justify-center rounded-lg text-gray-700"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            {undone.map(({ kind, label }) => {
              const m = byKind.get(kind);
              return (
                <div key={kind} className="flex items-center justify-between gap-3 border-t border-gray-100 py-2">
                  <span className="min-w-0">
                    <b className="block text-[15px] text-gray-900">{label}</b>
                    {MILESTONE_HINT[kind] && <span className="block text-xs text-gray-500">{MILESTONE_HINT[kind]}</span>}
                    {(m?.planned_on || isSupervisorOrAbove) && (
                      <span className="block text-xs text-gray-500">{m?.planned_on ? `Planned ${short(m.planned_on)}` : 'Not planned'}</span>
                    )}
                  </span>
                  {confirmKind === kind ? (
                    <span className="flex shrink-0 items-center gap-1.5" role="group" aria-label={`Confirm ${label} done today`}>
                      <button
                        type="button"
                        disabled={markDone.isPending}
                        onClick={() => markDone.mutate({ kind })}
                        className="min-h-11 rounded-lg bg-primary-600 px-4 text-sm font-semibold text-white disabled:opacity-60"
                      >
                        Yes, done today
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmKind(null)}
                        className="min-h-11 min-w-11 rounded-lg px-2 text-sm text-gray-600"
                      >
                        No
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmKind(kind)}
                      className="min-h-11 shrink-0 rounded-lg border border-gray-300 px-4 text-sm font-semibold text-gray-800"
                    >
                      Done today
                    </button>
                  )}
                </div>
              );
            })}
        </PhoneSheet>
      )}
    </div>
  );
}

function PhoneWaterCheck({
  farmId,
  crop,
  editing,
  canDelete,
  onClose,
}: {
  farmId: string;
  crop: Crop;
  editing: WaterCheckResponse | null;
  canDelete: boolean;
  onClose: () => void;
}) {
  const { create, update, remove } = useWaterCheckMutations(farmId, crop.id);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const mutation = editing ? update : create;
  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-white" role="dialog" aria-modal="true" aria-label={editing ? 'Edit water check' : 'Water check'} data-water-check>
      <header className="flex shrink-0 items-center gap-2 border-b border-gray-200 px-4 pb-3 pt-2">
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          disabled={mutation.isPending && !mutation.isPaused}
          className="-ml-2 flex min-h-11 min-w-11 items-center justify-center rounded-lg text-gray-900 active:bg-gray-100 disabled:opacity-40"
        >
          <X className="h-6 w-6" aria-hidden="true" />
        </button>
        <h2 className="text-[17px] font-bold text-gray-900">{editing ? 'Edit water check' : 'Water check'}</h2>
      </header>
      <div className="flex min-h-0 flex-1 flex-col px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <WaterCheckForm
          crop={crop}
          initial={editing ?? undefined}
          phone
          busy={mutation.isPending}
          paused={mutation.isPaused}
          error={mutation.error}
          submitLabel={editing ? 'Save changes' : 'Save check'}
          onCancel={onClose}
          onSubmit={({ measured_at, measuredAtChanged, values }) =>
            editing
              ? update.mutate({ id: editing.id, body: { ...(measuredAtChanged ? { measured_at } : {}), ...values } }, { onSuccess: onClose })
              : create.mutate({ cycle_id: crop.id, measured_at, ...values }, { onSuccess: onClose })
          }
        />
        {editing && canDelete && (
          <div className="mt-4 shrink-0 border-t border-gray-200 pt-4">
            {confirmDelete ? (
              <span className="flex items-center gap-2" role="group" aria-label="Confirm delete">
                <button
                  type="button"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(editing.id, { onSuccess: onClose })}
                  className="min-h-[48px] flex-1 rounded-lg bg-red-600 px-3 text-base font-semibold text-white disabled:opacity-60"
                >
                  Yes, delete this check
                </button>
                <button type="button" onClick={() => setConfirmDelete(false)} className="min-h-[48px] px-3 text-base text-gray-700">
                  No
                </button>
              </span>
            ) : (
              <button type="button" onClick={() => setConfirmDelete(true)} className="min-h-[48px] w-full text-base font-semibold text-red-700">
                Delete this check
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
